import React, { useEffect, useState } from "react";
import {
  MdEdit,
  MdDelete,
  MdAdd,
  MdCategory,
  MdVideoLibrary,
} from "react-icons/md";
import { toast } from "react-toastify";
import AddCategoryModal from "./AddCategoryModal";
import EditCategoryModal from "./EditCategoryModal";
import PageVideoModal from "./PageVideoModal";
import { adminApiService } from "services/api";

export default function CategoryTable() {
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showEditCategory, setShowEditCategory] = useState(false);
  const [showPageVideoModal, setShowPageVideoModal] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [editableCategory, setEditableCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const toggleAddCategory = () => {
    setShowAddCategory(!showAddCategory);
  };

  const toggleEditCategory = (category) => {
    setEditableCategory(category);
    setShowEditCategory(!showEditCategory);
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm("Are you sure you want to delete this category?")) {
      try {
        const response = await adminApiService.deleteCategory(categoryId);

        if (!response.ok) throw new Error("Failed to delete the category.");
        toast.success("Category deleted successfully!");
        fetchCategories();
      } catch (error) {
        console.error("Delete error:", error);
        toast.error("Error deleting the category.");
      }
    }
  };

  const handleToggleMenu = async (categoryId, currentStatus, existingVideo) => {
    console.log(currentStatus, existingVideo);
    if (
      !currentStatus &&
      (existingVideo === "false" || existingVideo == null)
    ) {
      setSelectedCategoryId(categoryId);
      setShowPageVideoModal(true);
      return;
    }

    try {
      const response = await adminApiService.updateMenuCategoryStatus(
        categoryId,
        { showInMenu: !currentStatus }
      );
      if (!response.ok) throw new Error("Failed to update menu status.");
      toast.success("Menu status updated successfully!");
      fetchCategories();
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Error updating menu status.");
    }
  };

  const handleEditVideo = (category) => {
    setSelectedCategoryId(category.id);
    setShowPageVideoModal(true);
  };

  const handlePageVideoSubmit = async (categoryId) => {
    try {
      await fetchCategories();
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Error updating category.");
    }
  };

  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      const response = await adminApiService.getAllCategories();

      if (!response.ok) throw new Error("Failed to fetch categories.");
      const data = await response.json();

      setCategories(data);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Error fetching categories.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}

      {/* Add / Edit Modals */}
      {showAddCategory && (
        <AddCategoryModal
          toggleAddCategory={toggleAddCategory}
          showAddCategory={showAddCategory}
          onCategoryAdded={fetchCategories}
        />
      )}
      {showEditCategory && editableCategory && (
        <EditCategoryModal
          category={editableCategory}
          toggleModal={() => toggleEditCategory(null)}
          showModal={showEditCategory}
          onCategoryUpdated={fetchCategories}
          categoryId={editableCategory.id}
        />
      )}
      <PageVideoModal
        isOpen={showPageVideoModal}
        onClose={() => setShowPageVideoModal(false)}
        onSubmit={handlePageVideoSubmit}
        categoryId={selectedCategoryId}
      />

      {/* Categories Table */}
      <div className="rounded-lg bg-white p-6 shadow-sm">
        <div className="overflow-x-auto">
          <div className="mb-5 flex items-center justify-end">
            <button
              onClick={toggleAddCategory}
              className="flex items-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-white transition hover:bg-primary/90"
            >
              <MdAdd className="text-lg" />
              Add Category
            </button>
          </div>
          <table className="w-full table-auto border-collapse">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="whitespace-nowrap rounded-tl-lg px-6 py-3 text-xs font-medium uppercase text-gray-700">
                  Category Name
                </th>
                <th className="whitespace-nowrap px-6 py-3 text-xs font-medium uppercase text-gray-700">
                  Show in Menu
                </th>
                <th className="whitespace-nowrap px-6 py-3 text-xs font-medium uppercase text-gray-700">
                  Page Video
                </th>
                <th className="whitespace-nowrap rounded-tr-lg px-6 py-3 text-xs font-medium uppercase text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan="4" className="px-6 py-10 text-center">
                    <div className="flex justify-center">
                      <div className="border-t-transparent h-8 w-8 animate-spin rounded-full border-4 border-primary"></div>
                    </div>
                  </td>
                </tr>
              ) : categories.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-10 text-center">
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <div className="rounded-full bg-gray-100 p-3">
                        <MdCategory className="text-2xl text-gray-400" />
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium text-gray-900">
                          No categories found
                        </p>
                        <p className="mt-1 text-sm text-gray-500">
                          Get started by creating a new category
                        </p>
                      </div>
                      <button
                        onClick={toggleAddCategory}
                        className="mt-2 flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-primary/90"
                      >
                        <MdAdd className="text-lg" />
                        Add Category
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                categories.map((category) => (
                  <tr key={category.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10">
                          <MdCategory className="text-lg text-primary" />
                        </div>
                        <span className="font-medium text-gray-900">
                          {category.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <label className="relative inline-flex cursor-pointer items-center">
                        <input
                          type="checkbox"
                          className="peer sr-only"
                          checked={category.showInMenu}
                          onChange={() =>
                            handleToggleMenu(
                              category.id,
                              category.showInMenu,
                              category.pageVideo
                            )
                          }
                        />
                        <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20"></div>
                      </label>
                    </td>
                    <td className="px-6 py-4">
                      {category.pageVideo !== "false" &&
                      category.pageVideo !== null ? (
                        <div className="flex items-center gap-3">
                          <div className="relative h-16 w-28 overflow-hidden rounded-lg">
                            <video
                              src={`${process.env.REACT_APP_API_URL_Plain}/config/uploads/page_video/${category.pageVideo}`}
                              className="h-full w-full object-cover"
                              controls
                            />
                          </div>
                          <button
                            onClick={() => handleEditVideo(category)}
                            className="rounded p-1.5 text-gray-500 transition hover:bg-gray-100 hover:text-primary"
                            title="Edit Video"
                          >
                            <MdEdit size={18} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-gray-500">
                          <MdVideoLibrary className="text-lg" />
                          <span className="text-sm">No video</span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => toggleEditCategory(category)}
                          className="rounded p-1.5 text-gray-500 transition hover:bg-gray-100 hover:text-primary"
                          title="Edit"
                        >
                          <MdEdit size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(category.id)}
                          className="rounded p-1.5 text-gray-500 transition hover:bg-red-50 hover:text-red-500"
                          title="Delete"
                        >
                          <MdDelete size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
